import { memo, useCallback, useMemo } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  NEW_TEMP_ID,
  changeReportTemplate,
  checkSaveDisabled,
  deleteReportTemplate,
  discardChanges,
  firstReportTemplateSelector,
  reportTemplateMapSelector,
  saveReportTemplate,
  secondReportTemplateSelector,
  setFirstReportTemplate,
  setSecondReportTemplate,
} from 'slices/reportTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { DrawerCallback } from 'types/utils.type';
import type { TReportTemplate } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import DetailContent from './sections/ReportTemplate';

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const ReportTemplateDrawer = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();

  const firstReportTemplate = useAppSelector(firstReportTemplateSelector);
  const secondReportTemplate = useAppSelector(secondReportTemplateSelector);
  const reportTemplateMap: Record<string, TReportTemplate> = useAppSelector(
    reportTemplateMapSelector
  );

  const setFirstResource = useCallback(
    (reportTemplate: TReportTemplate) =>
      dispatch(setFirstReportTemplate(reportTemplate)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (reportTemplate: TReportTemplate) =>
      dispatch(setSecondReportTemplate(reportTemplate)),
    [dispatch]
  );

  useSelectResource(
    'objectId',
    reportTemplateMap,
    firstReportTemplate,
    secondReportTemplate,
    setFirstResource,
    setSecondResource,
    NEW_TEMP_ID
  );

  const firstReportTemplateSaveDisabled = useMemo(
    () => dispatch(checkSaveDisabled(true)),
    [firstReportTemplate]
  );

  const secondReportTemplateSaveDisabled = useMemo(
    () => dispatch(checkSaveDisabled(false)),
    [secondReportTemplate]
  );

  const onDelete = useCallback((reportTemplate: TReportTemplate) => {
    dispatch(deleteReportTemplate(reportTemplate.id));
  }, []);

  const onSave = useCallback(
    (isFirstDrawer: boolean, reportTemplate: TReportTemplate) => {
      dispatch(
        saveReportTemplate({
          isFirstReportTemplate: isFirstDrawer,
          reportTemplate,
        })
      );
    },
    []
  );

  const onClose = useCallback(
    (isFirstDrawer: boolean) =>
      confirmDiscardPaddingChanges(isFirstDrawer, () => {
        if (isFirstDrawer) {
          dispatch(setFirstReportTemplate(undefined));
        } else {
          dispatch(setSecondReportTemplate(undefined));
        }
      }),
    []
  );

  const onChange = useCallback<DrawerCallback<TReportTemplate>>(
    (isFirstDrawer, key, value) =>
      dispatch(changeReportTemplate({ isFirstDrawer, key, value })),
    []
  );
  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: firstReportTemplate?.name || '',
        content: (
          <DetailContent
            data={firstReportTemplate}
            onChange={(key, value) => onChange(true, key, value)}
          />
        ),
        drawerOpen: !!firstReportTemplate,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('report_template_confirmation_delete')}
            handleDelete={() => onDelete(firstReportTemplate!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: true,
                  id: firstReportTemplate!.id,
                })
              )
            }
            handleSave={() => onSave(true, firstReportTemplate!)}
            isSaveDisable={firstReportTemplateSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(true),
      }}
      secondDrawer={{
        title: secondReportTemplate?.name || '',
        content: (
          <DetailContent
            data={secondReportTemplate}
            onChange={(key, value) => onChange(false, key, value)}
          />
        ),
        drawerOpen: !!secondReportTemplate,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('report_template_confirmation_delete')}
            handleDelete={() => onDelete(secondReportTemplate!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: false,
                  id: secondReportTemplate!.id,
                })
              )
            }
            handleSave={() => onSave(false, secondReportTemplate!)}
            isSaveDisable={secondReportTemplateSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(false),
      }}
    />
  );
};

export default memo(ReportTemplateDrawer);
